import { useCallback, useState, useSyncExternalStore } from "react";

/** Depois de "Agora não", o banner nao insiste por este periodo. */
export const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
export const DISMISS_STORAGE_KEY = "ilume:pwa-install-dismissed-at";

/** Evento nao padronizado do Chromium (Chrome/Edge/Android). */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/* ---- store de modulo -------------------------------------------------------
 * O navegador dispara `beforeinstallprompt` UMA vez por carregamento, cedo.
 * Se a captura vivesse dentro do hook, componentes montados depois (ex.: a
 * tela Perfil) perderiam o evento. Por isso o listener e global
 * (`captureInstallPrompt`, chamado em main.tsx) e os hooks so assinam. */
let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

let captured = false;
export function captureInstallPrompt(): void {
  if (captured || typeof window === "undefined") return;
  captured = true;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // guarda para disparar no clique, nao no mini-infobar
    deferred = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    installed = true;
    emit();
  });
}

/** So para testes: volta o store ao estado inicial. */
export function resetInstallPromptForTests(): void {
  deferred = null;
  installed = false;
  listeners.clear();
}

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

function isStandaloneNow(): boolean {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia?.("(display-mode: standalone)").matches === true || nav.standalone === true;
}

function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ se identifica como Mac com touch.
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const webkitOnly = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return ios && webkitOnly;
}

function recentlyDismissed(now = Date.now()): boolean {
  try {
    const at = Number(window.localStorage.getItem(DISMISS_STORAGE_KEY));
    return Number.isFinite(at) && at > 0 && now - at < DISMISS_COOLDOWN_MS;
  } catch {
    return false; // storage indisponivel (aba privada): so nao lembra
  }
}

function rememberDismiss(): void {
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()));
  } catch {
    /* sem storage: aceitavel */
  }
}

export interface InstallPrompt {
  /** Chromium entregou o evento: da para abrir o dialogo nativo. */
  canInstall: boolean;
  /** Safari iOS: nao ha evento; mostrar instrucao manual. */
  isIos: boolean;
  /** Ja roda como app instalado. */
  isStandalone: boolean;
  /** Banner deve aparecer (instalavel, nao instalado, nao dispensado). */
  shouldOffer: boolean;
  install: () => Promise<void>;
  /** Esconde o banner pelo periodo de cooldown (nao afeta o item do Perfil). */
  dismiss: () => void;
}

export function useInstallPrompt(): InstallPrompt {
  const event = useSyncExternalStore(subscribe, () => deferred);
  const wasInstalled = useSyncExternalStore(subscribe, () => installed);
  const [dismissed, setDismissed] = useState(recentlyDismissed);
  const [isIos] = useState(isIosSafari);
  const [standaloneAtLoad] = useState(isStandaloneNow);

  const install = useCallback(async () => {
    if (!deferred) return;
    const current = deferred;
    await current.prompt();
    const { outcome } = await current.userChoice;
    deferred = null; // o evento so pode ser usado uma vez
    emit();
    if (outcome === "dismissed") {
      rememberDismiss();
      setDismissed(true);
    }
  }, []);

  const dismiss = useCallback(() => {
    rememberDismiss();
    setDismissed(true);
  }, []);

  const canInstall = event !== null;
  const isStandalone = standaloneAtLoad || wasInstalled;
  return {
    canInstall,
    isIos,
    isStandalone,
    shouldOffer: !isStandalone && !dismissed && (canInstall || isIos),
    install,
    dismiss,
  };
}
